<script lang="ts">
import { defineComponent, PropType, ref, onMounted } from 'vue'
import MaterialIcon from '@/components/_core/MaterialIcon.vue'
import {
  themePath,
  documentsPath,
  videosPath,
  eventsPath,
  rankingIndexPath,
  mypagePath,
} from '@/generated/railsRoutes'

import type { MaterialIconName } from '@/components/_core/MaterialIcon.vue'

export default defineComponent({
  name: 'GlobalNavigation',

  components: {
    MaterialIcon,
  },

  props: {
    items: {
      type: Array as PropType<ThemeItem[]>,
      required: true,
    },
    /**
     * 現在ページが属するテーマの id（サーバ解決値）。
     * isActive 判定（旧 `theme == selected_theme`）に使う。
     */
    selectedThemeId: {
      type: Number as PropType<Maybe<number>>,
      default: null,
    },
  },

  setup(props) {
    const navigationItems = ref<GlobalNavigationItem[]>([])
    const url = new URL(window.location.href)

    onMounted(() => {
      // view_props で受け取った themes 情報を GlobalNavigationItem 型に変換する。
      // props.items は Rails 側で navigation 絞り込み＋priority 降順済み。
      // priority を持つテーマのみを対象に、先頭を「顔」、それ以降を「もっと見る」に集約する。
      const themeNavigations = props.items.filter(v => v.priority != null)
      const [topThemeItem, ...restThemeItems] = themeNavigations

      const secondaryNavItems = restThemeItems.map(v => ({
        label: v.title,
        to: themePath({ id: v.id }),
        isActive: getIsActiveByThemeId(v.id),
      }))

      // Theme テーブルの優先度が一番高いもの（admin で設定）を先頭に配置。
      // priority を持つテーマが 0 件だと topThemeItem が undefined になるため、参照はガード内で行う。
      if (topThemeItem) {
        navigationItems.value.push({
          label: topThemeItem.title,
          iconName: getIconNameByThemeTitle(topThemeItem.title),
          to: themePath({ id: topThemeItem.id }),
          priority: topThemeItem.priority,
          isActive: getIsActiveByThemeId(topThemeItem.id),
          isSignInOnly: false,
          isShow: {
            pc: true,
            sp: true,
          },
        })
      }

      // ノウハウ資料・動画（PC のみ表示）
      navigationItems.value.push({
        label: 'お役立ち・ノウハウ',
        to: '',
        children: {
          items: [
            {
              label: 'お役立ち資料',
              to: documentsPath(),
              isActive: url.pathname.startsWith(documentsPath()),
            },
            {
              label: 'ノウハウ動画',
              to: videosPath(),
              isActive: url.pathname.startsWith(videosPath()),
            },
          ],
        },
        isActive: url.pathname.startsWith(documentsPath()) || url.pathname.startsWith(videosPath()),
        isSignInOnly: false,
        isShow: {
          pc: true,
          sp: false,
        },
      })

      // ノウハウ動画（SP のみ表示）
      navigationItems.value.push({
        label: 'ノウハウ動画',
        iconName: 'VideoOutline',
        to: videosPath(),
        isActive: url.pathname.startsWith(videosPath()),
        isSignInOnly: false,
        isShow: {
          pc: false,
          sp: true,
        },
      })

      // イベント
      navigationItems.value.push({
        label: 'イベント・セミナー',
        iconName: 'Calendar',
        to: eventsPath(),
        isActive: url.pathname.includes('event'),
        isSignInOnly: false,
        isShow: {
          pc: true,
          sp: true,
        },
      })

      // もっと見る
      navigationItems.value.push({
        label: 'もっと見る',
        to: '',
        children: {
          items: [
            ...secondaryNavItems,
            {
              label: 'ランキング',
              to: rankingIndexPath(),
              isActive: url.pathname.startsWith(rankingIndexPath()),
            },
          ],
        },
        // 畳んだテーマ（セミナーレポート等）やランキングのページでは「もっと見る」自体をアクティブ表示する。
        isActive: secondaryNavItems.some(v => v.isActive) || url.pathname.startsWith(rankingIndexPath()),
        isSignInOnly: false,
        isShow: {
          pc: true,
          sp: false,
        },
      })

      navigationItems.value.forEach(v => {
        if (v.children) {
          Object.assign(v.children, { isShow: false })
        }
      })
    })

    const hasSecondaryNavigation = (parentItem: GlobalNavigationItem): boolean => {
      if (!parentItem.children || !parentItem.children.items) {
        return false
      }

      return !!parentItem.children.items.length
    }

    const showSecondaryNavigation = (parentItem: GlobalNavigationItem) => {
      if (parentItem.children && hasSecondaryNavigation(parentItem)) parentItem.children.isShow = true
    }

    const hideSecondaryNavigation = (parentItem: GlobalNavigationItem) => {
      if (parentItem.children && hasSecondaryNavigation(parentItem)) parentItem.children.isShow = false
    }

    /**
     * テーマタイトルから SP 用のアイコン（Material Design Icons）名を取得する（@/components/_core/MaterialIcon.vue 参照）
     *
     * @param title テーマタイトル
     * @returns マテリアルアイコンコンポーネント名
     *
     * @see @/components/_core/MaterialIcon.vue
     * @see https://pictogrammers.com/library/mdi/
     */
    function getIconNameByThemeTitle(title: string): MaterialIconName | null {
      return title.includes('インタビュー')
        ? 'ForumOutline'
        : null
    }

    /**
     * 現在開いているページが指定されたテーマに紐づくコンテンツのページかどうか
     *
     * @param themeId 指定のテーマ ID
     */
    function getIsActiveByThemeId(themeId: number): boolean {
      return (url.pathname.includes('theme') || url.pathname.includes('report') || url.pathname.includes('story')) && themeId === props.selectedThemeId
    }

    return {
      // routes
      mypagePath,

      // data
      navigationItems,

      // methods
      hasSecondaryNavigation,
      showSecondaryNavigation,
      hideSecondaryNavigation,
    }
  },
})
</script>

<template>
  <nav
    ref="global-navigation"
    class="f-navigation__global">
    <div class="f-navigation__global--body">
      <div class="o-list__globalNavigation">
        <ul class="o-list__globalNavigation--items">
          <template
            v-for="(item, i) in navigationItems"
            :key="i">
            <li
              v-if="item.isShow?.pc || item.isShow?.sp"
              class="o-list__globalNavigation--item"
              :data-pc="item.isShow.pc"
              :data-sp="item.isShow.sp"
              @mouseover="showSecondaryNavigation(item)"
              @mouseout="hideSecondaryNavigation(item)">
              <div
                class="o-action__globalNavigation"
                :data-active="item.isActive"
                :data-secondary-nav-opened="item.children?.isShow ?? false">
                <component
                  :is="item.to ? 'a' : 'span'"
                  class="o-action__globalNavigation--item"
                  :href="item.to || undefined"
                  :data-ga-action="item.label">
                  <span
                    v-if="item.iconName"
                    class="o-action__globalNavigation--icon">
                    <MaterialIcon :name="item.iconName" />
                  </span>
                  <span class="o-action__globalNavigation--label">
                    {{ item.label }}
                  </span>
                </component>
              <!-- /.o-action__globalNavigation --></div>

              <section
                v-if="hasSecondaryNavigation(item)"
                class="f-section__secondaryNavigation"
                :data-opened="item.children?.isShow ?? false">
                <div class="f-section__secondaryNavigation--body">
                  <div
                    class="o-list__secondaryNavigation"
                    :style="{ '--cols': item.children?.items?.length ?? 0 }">
                    <ul class="o-list__secondaryNavigation--items">
                      <li
                        v-for="(childItem, j) in item.children?.items ?? []"
                        :key="j"
                        class="o-list__secondaryNavigation--item">
                        <div
                          class="o-action__secondaryNavigation"
                          :data-active="childItem.isActive">
                          <a
                            class="o-action__secondaryNavigation--item"
                            :href="childItem.to">
                            {{ childItem.label }}
                          </a>
                        <!-- /.o-action__secondaryNavigation --></div>
                      </li>
                    </ul>
                  <!-- /.o-list__secondaryNavigation --></div>
                <!-- /.f-section__secondaryNavigation body --></div>
              <!-- /.f-section__secondaryNavigation --></section>
            </li>
          </template>
        </ul>
      <!-- /.o-list__globalNavigation --></div>
    <!-- /.f-navigation__global body --></div>
  <!-- /.f-navigation__global --></nav>
</template>

<style lang="sass" scoped>
$secondary-navigation-bgcolor: #fdf1e0

// foundation
.f-navigation__global
  position: relative

  & &--body
    width: 100%
    max-width: #{$global-contents-width-pc}px
    margin: 0 auto

.f-section__secondaryNavigation
  width: 100%
  position: absolute
  top: #{$header-secondary-height-pc}px
  left: 0
  transform: translateY(-8px)
  background-color: rgba($secondary-navigation-bgcolor, 1)
  box-shadow: $drop-bottom-shadow
  opacity: 0
  visibility: hidden
  +transition

  &[data-opened="true"]
    transform: translateY(0)
    opacity: 1
    visibility: visible

  & &--body
    width: 100%
    max-width: #{$global-contents-width-pc}px
    margin: 0 auto
    padding: 1.5em 0

  +breakpoint($max-media: tablet)
    display: none

// object
.o-list__globalNavigation
  $root: &
  & &--items
    display: flex
    justify-content: space-between

  & &--item
    flex-grow: 1

    &[data-pc="true"]
      display: block

    &[data-pc="false"]
      display: none

  +breakpoint($max-media: tablet)
    #{$root}--item
      &[data-sp="true"]
        display: block

      &[data-sp="false"]
        display: none

.o-list__secondaryNavigation
  & &--items
    width: 100%
    display: flex
    flex-wrap: wrap
    align-items: center
    justify-content: center
    gap: 1em

  & &--item
    line-height: 1

.o-action__globalNavigation
  $root: &
  & &--item
    display: block
    position: relative
    line-height: #{$header-secondary-height-pc}px
    color: $font-xx-dark-gray
    text:
      align: center
      decoration: none
    border:
      top-left-radius: $border-radius
      top-right-radius: $border-radius
    cursor: pointer
    +transition

    &::before
      content: ""
      width: 100%
      height: 0
      display: block
      position: absolute
      bottom: 0
      left: 0
      border-bottom: 2px solid $border-dark-orange
      opacity: 0
      +transition

    +mouseover
      color: $font-dark-orange
      background-color: rgba($secondary-navigation-bgcolor, 1)

  & &--icon
    display: none

  & &--label
    line-height: 1
    font-size: $font-small

  &[data-secondary-nav-opened="true"]
    #{$root}--item
      color: $font-dark-orange
      background-color: rgba($secondary-navigation-bgcolor, 1)

  &[data-active="true"]
    #{$root}--item
      color: $font-dark-orange

    #{$root}--label
      font-weight: bold

    &[data-secondary-nav-opened="false"]
      #{$root}--item
        &::before
          opacity: 1

  +breakpoint($max-media: tablet)
    #{$root}--item
      height: #{$header-secondary-height-sp}px
      display: flex
      flex-direction: column
      justify-content: center
      align-items: center
      line-height: 1
      letter-spacing: -0.05em
      text-align: center
      padding: 0

    #{$root}--icon
      width: 22px
      height: 22px
      display: block
      color: $font-gray
      margin-bottom: 0.3em

    #{$root}--label
      font-size: 1.1rem

    &[data-active="false"]
      #{$root}--item
        &::before
          display: none

        +mouseover
          color: $font-xx-dark-gray // negative font color for PC

    &[data-active="true"]
      #{$root}--icon
        color: $font-dark-orange

.o-action__secondaryNavigation
  $root: &

  & &--item
    display: block
    line-height: 1
    color: $font-xx-dark-gray
    font-size: $font-small
    text:
      align: center
      decoration: none
    padding: 1em 2em
    border-radius: 100vh
    +transition

    +mouseover
      text-decoration: underline
      background-color: rgba($bg-white, 1)

  &[data-active="true"]
    #{$root}--item
      color: $font-dark-orange
      font-weight: bold
</style>
